import { useState } from "react";
import { toast } from "sonner";
import { AideFieldValues, aideFormSchema } from "../lib/schemas/aide-form-schema";
import { useCurrentUser } from "../lib/hooks/useCurrentUser";
import { useMotifsSupport } from "../lib/hooks/useMotifsSupport";
import { useCreateTicketSupport } from "../lib/hooks/useCreateTicketSupport";
import { Alert } from "../components/ui/alert";
import Title from "../components/ui/Title";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { CheckCircle2, Send } from "lucide-react";

const AidePage = () => {
  const { data: user } = useCurrentUser();
  const { data: motifs } = useMotifsSupport();
  const [success, setSuccess] = useState(false);
  const form = useForm<AideFieldValues>({
    resolver: zodResolver(aideFormSchema),
    defaultValues: { message: "", motifId: "" },
  });

  const { mutate: envoyer, isPending } = useCreateTicketSupport();

  const onSubmit = (data: AideFieldValues) => {
    envoyer(data, {
      onSuccess: () => {
        setSuccess(true);
        toast.success("Message envoyé");
      },
      onError: (err: any) => {
        console.error(err.response?.data || err);
        toast.error(`Une erreur s'est produite`);
      },
    });
  };

  return (
    <div className="mx-auto w-full max-w-4xl pb-8">
      <Title className="mb-7">Nous contacter</Title>

      {!!motifs && !!user ? (
        <section className="overflow-hidden rounded-2xl border border-slate-200 border-t-4 border-t-[#173f35] bg-white shadow-[0_14px_40px_-34px_rgba(15,23,42,0.5)]">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6 p-5 sm:p-7 lg:p-8">
              {/* Motif */}
              <FormField
                control={form.control}
                name="motifId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-slate-800">
                      Veuillez préciser le motif de votre demande
                    </FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-11 w-full border-slate-300 bg-white sm:max-w-[520px]">
                          <SelectValue placeholder="Sélectionnez un motif" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {motifs.map((mot) => (
                          <SelectItem key={mot.id} value={mot.id}>
                            {mot.nom}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Message */}
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-slate-800">Veuillez détailler votre demande</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={8}
                        placeholder="Votre message..."
                        className="min-h-56 resize-y border-slate-300 bg-white px-4 py-3 leading-6 focus-visible:ring-[#286351]"
                        {...field}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
              {!success && (
                <div>
                  <Button
                    loading={isPending}
                    type="submit"
                    className="w-full bg-[#173f35] hover:bg-[#23594a] sm:w-auto"
                    icon={<Send className="h-4 w-4" />}
                  >
                    Envoyer le message
                  </Button>
                </div>
              )}
            </form>
          </Form>

          {success && (
            <Alert
              variant="default"
              className="mx-5 mb-5 flex items-center gap-3 border-[#b8d2c3] bg-[#e4efe9] text-[#173f35] sm:mx-7 sm:mb-7 lg:mx-8 lg:mb-8"
            >
              <CheckCircle2 className="h-5 w-5 shrink-0" />
              <span>Votre message a bien été envoyé ! Vous recevrez une réponse par email rapidement.</span>
            </Alert>
          )}
        </section>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 border-t-4 border-t-[#173f35] bg-white p-5 sm:p-7 lg:p-8">
          <Skeleton className="mb-6 h-11 w-full max-w-[520px]" />
          <Skeleton className="h-56 w-full" />
          <Skeleton className="mt-6 h-10 w-full sm:w-44" />
        </div>
      )}
    </div>
  );
};

export default AidePage;
